import { useMemo } from 'react';
import { Dropdown, DropdownOption, DropdownProps } from '@bitrise/bitkit';
import getTimeZoneOffsetInMin from '@/utils/getTimeZoneOffsetInMin';

interface TimeZoneDropdownProps extends DropdownProps<any> {
  locale: string;
  defaultValue?: string;
  onTimeZoneChange(timeZoneLocation: string): void;
}

export const formatTimeZoneLabel = (timeZone: string) => {
  const totalMinutes = getTimeZoneOffsetInMin(timeZone, new Date());
  const hours = Math.floor(Math.abs(totalMinutes) / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (Math.abs(totalMinutes) % 60).toString().padStart(2, '0');
  const sign = totalMinutes < 0 ? '-' : '+';

  return `UTC${sign}${hours}:${minutes} ${timeZone}`;
};

// This is needed until all browsers implement the new spelling
const fixKyivTimeZoneName = (timeZones: string[], timeZoneMap: Map<string, string>) => {
  const oldName = 'Europe/Kiev';
  const newName = 'Europe/Kyiv';
  const label = timeZoneMap.get(oldName);
  if (label) {
    timeZoneMap.delete(oldName);
    timeZoneMap.set(newName, label?.replace('Kiev', 'Kyiv'));
    timeZones.splice(timeZones.indexOf(oldName), 1, newName);
  }
};

export const getTimeZoneDropdownData = (): [string[], Map<string, string>] => {
  const timeZones = Intl.supportedValuesOf('timeZone');

  const map = new Map<string, string>();

  timeZones.forEach((timeZone) => {
    map.set(timeZone, formatTimeZoneLabel(timeZone));
  });

  fixKyivTimeZoneName(timeZones, map);

  return [timeZones, map];
};

const TimeZoneDropdown = ({ locale, onTimeZoneChange, ...rest }: TimeZoneDropdownProps) => {
  const [timeZones, timeZoneLabelMap] = useMemo(() => getTimeZoneDropdownData(), []);

  const handleTimeZoneChange = (timeZoneLocation: string) => onTimeZoneChange(timeZoneLocation);

  return (
    <Dropdown
      label="Time zone"
      value={locale}
      id="timeZoneDropdown"
      {...rest}
      onChange={(event) => handleTimeZoneChange(event.target.value as string)}
      required
    >
      <DropdownOption value="UTC">UTC</DropdownOption>
      {timeZones.map((timeZone) => (
        <DropdownOption key={timeZone} value={timeZone}>{`${timeZoneLabelMap.get(timeZone)}`}</DropdownOption>
      ))}
    </Dropdown>
  );
};

export default TimeZoneDropdown;
